import React, { useEffect } from 'react';

type ToastVariant = 'default' | 'success' | 'error' | 'warning';

interface ToastProps {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  onClose: () => void;
}

// Función para crear y mostrar un Toast
export const showToast = (
  message: string, 
  variant: ToastVariant = 'default', 
  duration: number = 5000
) => {
  // Crear un contenedor para el toast si no existe
  let toastContainer = document.getElementById('toast-container');
  
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'toast-container';
    toastContainer.className = 'fixed top-4 right-4 z-50 flex flex-col gap-2';
    document.body.appendChild(toastContainer);
  }
  
  // Crear un ID único para este toast
  const toastId = `toast-${Date.now()}`;
  
  // Crear un nuevo div para este toast
  const toastElement = document.createElement('div');
  toastElement.id = toastId;
  
  // Renderizar el componente Toast dentro del div
  const root = document.createElement('div');
  toastElement.appendChild(root);
  
  // Añadir el toast al contenedor
  toastContainer.appendChild(toastElement);
  
  // Función para cerrar este toast específico
  const closeToast = () => {
    const element = document.getElementById(toastId);
    if (element) {
      element.classList.add('opacity-0', 'translate-x-4');
      setTimeout(() => {
        element.remove();
        
        // Si no quedan más toasts, eliminar el contenedor
        if (toastContainer && toastContainer.children.length === 0) {
          toastContainer.remove();
        }
      }, 300);
    }
  };
  
  // Renderizar el componente Toast en el div
  const toast = document.createElement('div');
  
  // Determinar las clases según la variante
  let variantClasses = '';
  let iconSvg = '';
  
  switch (variant) {
    case 'success':
      variantClasses = 'bg-success/10 border-success/30 text-success';
      iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`;
      break;
    case 'error':
      variantClasses = 'bg-destructive/10 border-destructive/30 text-destructive';
      iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12" y2="16"></line></svg>`;
      break;
    case 'warning':
      variantClasses = 'bg-warning/10 border-warning/30 text-warning';
      iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12" y2="17"></line></svg>`;
      break;
    default:
      variantClasses = 'bg-card text-card-foreground border-border';
      iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="8"></line></svg>`;
  }
  
  toast.className = `p-4 rounded-md border shadow-md ${variantClasses} max-w-sm transition-all duration-300 opacity-0 translate-x-4`;
  toast.innerHTML = `
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <div class="flex-shrink-0">
          ${iconSvg}
        </div>
        <p class="text-sm font-medium">${message}</p>
      </div>
      <button class="ml-4 text-current opacity-70 hover:opacity-100" aria-label="Cerrar">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
  `;
  
  // Añadir el toast al elemento
  toastElement.appendChild(toast);
  
  // Retrasar la animación para que se muestre suavemente
  setTimeout(() => {
    toast.classList.remove('opacity-0', 'translate-x-4');
  }, 10);
  
  // Configurar el botón de cerrar
  const closeButton = toast.querySelector('button');
  if (closeButton) {
    closeButton.addEventListener('click', closeToast);
  }
  
  // Configurar el cierre automático
  if (duration > 0) {
    setTimeout(closeToast, duration);
  }
  
  // Devolver una función para cerrar programáticamente
  return closeToast;
};

// Componente React (por si se necesita usar como componente)
export const Toast: React.FC<ToastProps> = ({ 
  message, 
  variant = 'default', 
  duration = 5000,
  onClose 
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);
  
  // Determinar las clases según la variante
  let variantClasses = '';
  let Icon = null;
  
  switch (variant) {
    case 'success':
      variantClasses = 'bg-success/10 border-success/30 text-success';
      Icon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      );
      break;
    case 'error':
      variantClasses = 'bg-destructive/10 border-destructive/30 text-destructive';
      Icon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12" y2="16"></line>
        </svg>
      );
      break;
    case 'warning':
      variantClasses = 'bg-warning/10 border-warning/30 text-warning';
      Icon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12" y2="17"></line>
        </svg>
      );
      break;
    default:
      variantClasses = 'bg-card text-card-foreground border-border';
      Icon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12" y2="8"></line>
        </svg>
      );
  }
  
  return (
    <div className={`p-4 rounded-md border shadow-md ${variantClasses} max-w-sm`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0">
            {Icon && <Icon />}
          </div>
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button 
          onClick={onClose}
          className="ml-4 text-current opacity-70 hover:opacity-100" 
          aria-label="Cerrar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Toast; 